import { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { z } from 'zod'
import { useZodForm } from 'src/hooks/zod-form'
import { Box, Button, Autocomplete, TextField, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@emotion/react'
import { toast } from 'react-toastify'

interface AddBatchNumberInlineProps {
  positionId: EntityId
  componentId: EntityId
  onSubmit: (form: any) => void
  batchNumbers: string[]
  setMode: (mode: 'view' | 'edit') => void
}

const schema = z.object({
  commonBatchNumbers: z.array(z.string()).nullable(),
  componentIds: z.array(z.number()),
  positionIds: z.array(z.number()),
})

export const AddBatchNumberInline: FC<AddBatchNumberInlineProps> = ({
  positionId,
  componentId,
  onSubmit,
  batchNumbers = [],
  setMode,
}) => {
  const form = useZodForm(schema)
  const { t } = useTranslation(['common', 'position'])
  const theme = useTheme()

  const [newBatchNumbers, setNewBatchNumbers] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')

  const formValues = useMemo(
    () => ({
      positionIds: [parseInt(positionId as string)],
      componentIds: componentId ? [parseInt(componentId as string)] : [],
      commonBatchNumbers: batchNumbers || [],
    }),
    [positionId, componentId, batchNumbers],
  )

  useEffect(() => {
    form.setValue('positionIds', formValues.positionIds)
    if (componentId) {
      form.setValue('componentIds', formValues.componentIds)
    }
    if (batchNumbers && batchNumbers.length > 0) {
      form.setValue('commonBatchNumbers', formValues.commonBatchNumbers)
      setNewBatchNumbers(batchNumbers)
    }
  }, [])

  const handleSave = useCallback(async () => {
    const pendingValue = inputValue.trim()
    let finalBatchNumbers = [...newBatchNumbers]

    if (pendingValue && newBatchNumbers.includes(pendingValue)) {
      toast.warn(`"${pendingValue}" ${t('already-exists', { ns: 'position' })}`)
    }
    if (pendingValue && !newBatchNumbers.includes(pendingValue)) {
      finalBatchNumbers = [...newBatchNumbers, pendingValue]
      setNewBatchNumbers(finalBatchNumbers)
    }

    setInputValue('')
    form.setValue('commonBatchNumbers', finalBatchNumbers)

    await form.handleSubmit(data => {
      if (onSubmit) {
        onSubmit(data)
        setMode('view')
        setMode('view')
      }
    })()
  }, [form, inputValue, newBatchNumbers, onSubmit, setMode])

  const handleCancel = useCallback(() => {
    setNewBatchNumbers([])
    setMode('view')
  }, [setMode])

  const cleanupBatchNumbers = (values: string[]): string[] => {
    return Array.from(new Set(values.map(v => v.trim()).filter(v => v !== '')))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, padding: 2, width: '400px' }}>
      <Box className="pt-12-core">
        <Autocomplete<string, true, true, true>
          multiple
          freeSolo
          disableClearable
          options={[]}
          value={newBatchNumbers}
          filterSelectedOptions
          onChange={(_, newValue) => {
            setNewBatchNumbers(cleanupBatchNumbers(newValue))
          }}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue)
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                label={option}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={t('update-batch-numbers', { ns: 'position' })}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  const currentValue = inputValue.trim()

                  if (currentValue && newBatchNumbers.includes(currentValue)) {
                    toast.warn(`"${currentValue}" ${t('already-exists', { ns: 'position' })}`)
                  }
                }
              }}
            />
          )}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          {t('cancel', { ns: 'common' })}
        </Button>

        <Button variant="contained" color="primary" onClick={handleSave}>
          {t('save', { ns: 'common' })}
        </Button>
      </Box>
    </Box>
  )
}
